import { MODEL_URL, WASM_ROOT } from "./mediapipeConfig";

/**
 * The pose model, loaded ONCE per page and shared by every extraction (the upload-time one over
 * the picked video, the post-split one over frame PNGs). The upload screen starts the load the
 * moment it opens — before a clip is picked — so the 30 MB heavy model and the WASM runtime are
 * usually in by the time the analysis wants them, instead of the user sitting on "Loading the
 * pose model…" (Safari, first tester). Progress is published so the step can say what it is
 * waiting on and how far along it is; a failed load is forgotten so the next attempt retries.
 */

/** idle → runtime (WASM) → model (download) → warming (building the landmarker) → warm (one ready to hand over). */
export type PoseModelPhase = "idle" | "runtime" | "model" | "warming" | "warm" | "failed";

export type PoseModelProgress = {
  phase: PoseModelPhase;
  /** Model bytes downloaded so far, and the total when the server said. */
  loadedBytes: number;
  totalBytes: number | null;
  error?: string;
};

type Runtime = {
  fileset: unknown;
  PoseLandmarker: { createFromOptions: (fileset: any, options: any) => Promise<any> };
};

/** The model itself is NOT kept: the preload only pulls it through the browser cache (public,
 * max-age 3600) so the landmarker's own fetch of MODEL_URL is instant. The landmarker is built
 * exactly as it always was, from the URL — handing it the bytes was the one change on its side,
 * and Safari stalled at "Starting the pose model…" right after. */
export type PoseModelAssets = { runtime: Runtime; modelBytes: number };

/** Seams so the loader is testable without a browser or a network. */
export type PoseModelDeps = {
  loadRuntime: () => Promise<Runtime>;
  fetchModel: (url: string, onProgress: (loaded: number, total: number | null) => void) => Promise<Uint8Array>;
};

async function defaultLoadRuntime(): Promise<Runtime> {
  const { FilesetResolver, PoseLandmarker } = await import("@mediapipe/tasks-vision");
  const fileset = await FilesetResolver.forVisionTasks(WASM_ROOT);
  return { fileset, PoseLandmarker };
}

async function defaultFetchModel(url: string, onProgress: (loaded: number, total: number | null) => void): Promise<Uint8Array> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`pose model download failed (${res.status})`);
  const header = res.headers.get("content-length");
  const total = header ? Number(header) : null;
  if (!res.body) {
    const buf = new Uint8Array(await res.arrayBuffer());
    onProgress(buf.byteLength, buf.byteLength);
    return buf;
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress(loaded, total);
  }
  const out = new Uint8Array(loaded);
  let at = 0;
  for (const c of chunks) { out.set(c, at); at += c.byteLength; }
  return out;
}

export const defaultPoseModelDeps: PoseModelDeps = { loadRuntime: defaultLoadRuntime, fetchModel: defaultFetchModel };

let loading: Promise<PoseModelAssets> | null = null;
let progress: PoseModelProgress = { phase: "idle", loadedBytes: 0, totalBytes: null };
const listeners = new Set<(p: PoseModelProgress) => void>();

function publish(next: PoseModelProgress) {
  progress = next;
  listeners.forEach((l) => l(progress));
}

export function poseModelProgress(): PoseModelProgress {
  return progress;
}

export function subscribePoseModel(listener: (p: PoseModelProgress) => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Starts the load if it has not started (or the last one failed); otherwise hands back the one in flight. */
export function preloadPoseModel(deps: PoseModelDeps = defaultPoseModelDeps): Promise<PoseModelAssets> {
  if (loading) return loading;
  const run = (async () => {
    publish({ phase: "runtime", loadedBytes: 0, totalBytes: null });
    const runtime = await deps.loadRuntime();
    publish({ phase: "model", loadedBytes: 0, totalBytes: null });
    const bytes = await deps.fetchModel(MODEL_URL, (loaded, total) => publish({ phase: "model", loadedBytes: loaded, totalBytes: total }));
    const modelBytes = bytes.byteLength;
    publish({ phase: "warming", loadedBytes: modelBytes, totalBytes: modelBytes });
    return { runtime, modelBytes };
  })();
  loading = run.catch((e) => {
    // Forgotten, so the next call tries again (a flaky network is not a permanent state).
    loading = null;
    publish({ phase: "failed", loadedBytes: 0, totalBytes: null, error: e?.message ?? String(e) });
    throw e;
  });
  return loading;
}

export type RunningMode = "VIDEO" | "IMAGE";

async function buildLandmarker(runningMode: RunningMode, deps: PoseModelDeps): Promise<any> {
  const { runtime } = await preloadPoseModel(deps);
  return runtime.PoseLandmarker.createFromOptions(runtime.fileset, {
    baseOptions: { modelAssetPath: MODEL_URL },
    runningMode,
    numPoses: 1,
  });
}

// One landmarker built ahead of time per running mode: building it (the WASM graph over the
// 30 MB model) is the slow step after the download — the one Safari sat on as "Starting the
// pose model…" — so it is done in the background before anyone picks a clip.
const warm: Partial<Record<RunningMode, Promise<any>>> = {};

/** Builds a landmarker in the background and keeps it ready to hand over. Idempotent per mode. */
export function warmPoseLandmarker(runningMode: RunningMode = "VIDEO", deps: PoseModelDeps = defaultPoseModelDeps): Promise<any> {
  const existing = warm[runningMode];
  if (existing) return existing;
  const building = buildLandmarker(runningMode, deps).then((lm) => {
    if (warm[runningMode] === building) publish({ ...progress, phase: "warm" });
    return lm;
  });
  warm[runningMode] = building;
  building.catch(() => { if (warm[runningMode] === building) delete warm[runningMode]; });
  return building;
}

/** The warm landmarker if one is ready or being built, else a fresh one over the shared model. */
export async function createPoseLandmarker(runningMode: RunningMode, deps: PoseModelDeps = defaultPoseModelDeps): Promise<any> {
  const ready = warm[runningMode];
  if (ready) {
    delete warm[runningMode];
    return ready;
  }
  return buildLandmarker(runningMode, deps);
}

/** Closes a landmarker a run is done with and warms the next one, so the next clip is quick too.
 * (A VIDEO landmarker cannot be reused: its timestamps must only go forward, and a new clip
 * starts from zero.) */
export function releasePoseLandmarker(landmarker: { close: () => void }, runningMode: RunningMode, deps: PoseModelDeps = defaultPoseModelDeps): void {
  try { landmarker.close(); } catch { /* already closed */ }
  warmPoseLandmarker(runningMode, deps).catch(() => undefined);
}

/** Tests only: forget everything loaded. */
export function resetPoseModelForTests() {
  loading = null;
  for (const k of Object.keys(warm) as RunningMode[]) delete warm[k];
  progress = { phase: "idle", loadedBytes: 0, totalBytes: null };
  listeners.clear();
}
